import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';

import { UtilService } from '@service/util/util.service';
import { IS_NOT_EMPTY, IS_NOT_NULL_OR_UNDEFINED, RestService } from 'cargo-chief-commons';
import { UrlService } from '@service/url/url.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { DocsDialogComponent } from '@core/docs-dialog/docs-dialog.component';
import { TERMS_AND_CONDITIONS_URL } from '@domain/constants/app-variables';

@Component({
	selector: 'app-edit-account-details-dialog',
	templateUrl: 'edit-account-details-modal.template.html',
	styleUrls: ['edit-account-details-modal.template.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class EditAccountDetailsDialogComponent implements OnInit {
	userNameForm: UntypedFormGroup;
	isRequestInProgress = false;

	isUpdateBusinessName: boolean = false;
	isUpdateUserName: boolean = false;
	isUpdatePhoneNumber: boolean = false;
	isProfileUpdate: boolean = false;
	isUpdateMobileNumber: boolean = false;
	title: string = '';
	termsAgreed: boolean = true;

	constructor(
		public dialogRef: MatDialogRef<EditAccountDetailsDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: any,
		private urlService: UrlService,
		private restService: RestService,
		private utilService: UtilService,
		private _formBuilder: UntypedFormBuilder,
		private cargochief: Cargochief,
		public matDialog: MatDialog
	) {}

	closeDialog(): void {
		this.dialogRef.close();
	}

	ngOnInit() {
		this.isUpdateBusinessName = this.data.isUpdateBusinessName;
		this.isUpdateUserName = this.data.isUpdateUserName;
		this.isUpdatePhoneNumber = this.data.isUpdatePhoneNumber;
		this.isProfileUpdate = this.data.isProfileUpdate;
		this.isUpdateMobileNumber = this.data.isUpdateMobileNumber;
		this.title = this.data.title;
		if (this.utilService.isUserRoleOwner()) {
			this.termsAgreed = IS_NOT_NULL_OR_UNDEFINED(this.cargochief.TERMS_AGREED);
		}
		if (!this.isUpdateBusinessName) {
			this.setUserDetailsForm();
		} else {
			this.setUpdateBusinessNameForm();
		}
	}

	setUserDetailsForm() {
		const user: any = this.cargochief.getLoginSetupUser();
		this.userNameForm = this._formBuilder.group({
			firstName: [user.firstName],
			lastName: [user.lastName],
			phoneNumber: [IS_NOT_EMPTY(user.phoneNumber) ? user.phoneNumber.split(' ext. ')[0] : ''],
			phoneNumberExt: [IS_NOT_EMPTY(user.phoneNumber) ? user.phoneNumber.split(' ext. ')[1] : ''],
			mobileNumber: [IS_NOT_EMPTY(user.mobileNumber) ? user.mobileNumber : ''],
			termsAgreed: this.termsAgreed,
		});
		if (this.isUpdatePhoneNumber) {
			this.userNameForm.get('phoneNumber').setValidators([Validators.required]);
		}
		if (this.isUpdateUserName || this.isProfileUpdate) {
			this.userNameForm.get('firstName').setValidators([Validators.required]);
			this.userNameForm.get('lastName').setValidators([Validators.required]);
		}
	}

	setUpdateBusinessNameForm() {
		this.userNameForm = this._formBuilder.group({
			businessName: [this.cargochief.BUSINESS_NAME, Validators.required],
		});
	}

	submit() {
		if (this.userNameForm.valid) {
			if (!this.isUpdateBusinessName) {
				this.updateUserDetails();
			} else {
				this.updateBusinessName();
			}
		}
	}

	updateUserDetails() {
		this.isRequestInProgress = true;
		const url = this.urlService.updateUser();
		let payload = Object.assign({}, this.userNameForm.value);
		payload.userId = this.cargochief.USER_ID;
		payload.email = this.cargochief.getUser()['email'];
		payload.userType = this.cargochief.getUser()['userType'];

		if (IS_NOT_EMPTY(payload.phoneNumberExt)) {
			payload.phoneNumber = payload.phoneNumber + ' ext. ' + payload.phoneNumberExt;
			delete payload.phoneNumberExt;
		}
		this.restService.POST(url, payload).subscribe(
			(result) => {
				if (result != null) {
					this.utilService.showSnackbar(
						'User name updated successfully.',
						SNACKBAR.SUCCESS,
						SNACKBAR.ACTION
					);
				} else {
					this.utilService.showSnackbar(
						'Unable to update user name now. Please try again later.',
						SNACKBAR.WARNING,
						SNACKBAR.ACTION
					);
				}
				this.isRequestInProgress = false;
				this.dialogRef.close(result.data);
			},
			(error) => {
				// Don't display any HTML errors.
				this.utilService.showSnackbar(
					//check for html
					`Unable to update user name now. ${error.error}`,
					SNACKBAR.ERROR,
					SNACKBAR.ACTION
				);
				this.isRequestInProgress = false;
			}
		);
	}

	updateBusinessName() {
		this.isRequestInProgress = true;
		const payload = {
			accountId: this.cargochief.ACCOUNT_ID,
			userId: this.cargochief.USER_ID,
			businessName: this.userNameForm.get('businessName').value,
			firstName: this.cargochief.getUser().firstName,
			lastName: this.cargochief.getUser().lastName,
			phoneNumber: this.cargochief.getUser().phoneNumber,
		};
		const url = this.urlService.completeRegistration();
		this.restService.POST(url, payload).subscribe(
			(result) => {
				this.utilService.showSnackbar(
					'Business name updated successfully.',
					SNACKBAR.SUCCESS,
					SNACKBAR.ACTION
				);
				this.isRequestInProgress = false;
				this.dialogRef.close(this.userNameForm.get('businessName').value);
			},
			(error) => {
				this.utilService.showSnackbar(
					'Unable to update business name now.',
					SNACKBAR.ERROR,
					SNACKBAR.ACTION
				);
				this.isRequestInProgress = false;
			}
		);
	}

	openTermsAndConditionsDialog() {
		const dialogRef = this.matDialog.open(DocsDialogComponent, {
			width: '900px',
			data: {
				title: 'Terms & Conditions',
				url: TERMS_AND_CONDITIONS_URL.NON_ASCEND,
				cancelButtonText: 'Cancel',
				confirmButtonText: 'I Agree',
			},
		});
		dialogRef.afterClosed().subscribe((result) => {
			if (!this.utilService.isNullOrUndefined(result) && result) {
				this.userNameForm.get('termsAgreed').setValue(true);
			} else {
				this.userNameForm.get('termsAgreed').setValue(false);
			}
		});
	}
}
